import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Flame, Sparkles, Loader2, Lock, RotateCcw, Send, User, History, ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { bumpToolStat } from "@/lib/toolStats";
import { AiThinking } from "@/components/AiThinking";
import { CinematicVideo } from "@/components/CinematicVideo";
import { DialecticCore } from "@/components/DialecticCore";
import { ShareBar } from "@/components/ShareBar";

const DOUTE_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/zhn93q78_Le_Feu_du_Doute_Bouddha_prend.mp4";

const HIST_T = {
  fr: { title: "Tes convictions passées", exch: "échanges" },
  en: { title: "Your past convictions", exch: "exchanges" },
  es: { title: "Tus convicciones pasadas", exch: "intercambios" },
  nl: { title: "Je vorige overtuigingen", exch: "uitwisselingen" },
};

const SHARE_T = {
  fr: { convictionLabel: "Ma conviction", doubtLabel: "Le doute", assaults: (n) => `${n} assaut${n > 1 ? "s" : ""} encaissé${n > 1 ? "s" : ""}`, cta: "Et toi, tiendrais-tu tête au doute ?" },
  en: { convictionLabel: "My conviction", doubtLabel: "The doubt", assaults: (n) => `${n} assault${n > 1 ? "s" : ""} withstood`, cta: "And you, could you withstand the doubt?" },
  es: { convictionLabel: "Mi convicción", doubtLabel: "La duda", assaults: (n) => `${n} embate${n > 1 ? "s" : ""} resistido${n > 1 ? "s" : ""}`, cta: "Y tú, ¿resistirías la duda?" },
  nl: { convictionLabel: "Mijn overtuiging", doubtLabel: "De twijfel", assaults: (n) => `${n} aanval${n > 1 ? "len" : ""} doorstaan`, cta: "En jij, zou jij de twijfel weerstaan?" },
};

export default function FeuDuDoute() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [thesis, setThesis] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // {role:'devil'|'user', text}
  const [reply, setReply] = useState("");
  const [starting, setStarting] = useState(false);
  const [sending, setSending] = useState(false);
  const [locked, setLocked] = useState(false);
  const bottomRef = useRef(null);

  const fd = t("atelier.fd", { returnObjects: true }) || {};
  const presets = Array.isArray(fd.presets) ? fd.presets : [];
  const lang = () => (i18n.language || "fr").slice(0, 2);
  const ht = HIST_T[lang()] || HIST_T.fr;
  const st = SHARE_T[lang()] || SHARE_T.fr;
  const [history, setHistory] = useState([]);
  const devilCount = messages.filter((m) => m.role === "devil").length;

  const loadHistory = async () => {
    try { const { data } = await api.get("/doute/history"); setHistory(data.items || []); } catch { /* noop */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const openSession = async (id) => {
    try {
      const { data } = await api.get(`/doute/session/${id}`);
      setSessionId(data.session_id);
      setThesis(data.thesis);
      setMessages(data.messages || []);
      setLocked(false);
    } catch { toast.error(fd.error); }
  };

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, starting, sending]);

  const start = async (value) => {
    const th = ((value ?? thesis) || "").trim();
    if (!th || starting) return;
    setThesis(th);
    setStarting(true);
    setLocked(false);
    try {
      const { data } = await api.post("/doute/session", { thesis: th, lang: lang() });
      setSessionId(data.session_id);
      bumpToolStat("doute");
      setMessages([{ role: "devil", text: data.opening }]);
      loadHistory();
    } catch (e) {
      if (e.response?.status === 402) setLocked(true);
      else toast.error(formatApiErrorDetail(e.response?.data?.detail) || fd.error);
    } finally {
      setStarting(false);
    }
  };

  const send = async () => {
    const msg = reply.trim();
    if (!msg || sending || !sessionId) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setReply("");
    setSending(true);
    try {
      const { data } = await api.post("/doute/message", { session_id: sessionId, message: msg, lang: lang() });
      setMessages((m) => [...m, { role: "devil", text: data.reply }]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || fd.error);
    } finally {
      setSending(false);
    }
  };

  const reset = () => { setSessionId(null); setMessages([]); setThesis(""); setReply(""); };

  return (
    <div className="h-full overflow-y-auto scroll-thin">
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-14">
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.3em] text-red-300/80 mb-3">
            <Flame size={14} /> {fd.eyebrow}
          </span>
          <h1 className="font-display text-3xl sm:text-4xl text-gradient-orange leading-tight mb-3" data-testid="fd-title">{fd.title}</h1>
          <p className="text-foreground/60 leading-relaxed max-w-xl mx-auto text-sm sm:text-base">{fd.subtitle}</p>
        </div>

        {locked && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="glass rounded-2xl border border-primary/25 p-8 text-center frame-pulse" data-testid="fd-locked">
            <span className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-4"><Lock size={24} strokeWidth={1.6} /></span>
            <p className="font-display text-xl text-foreground/90 mb-2">{fd.locked}</p>
            <p className="text-sm text-foreground/55 leading-relaxed max-w-md mx-auto mb-6">{fd.lockedDesc}</p>
            <button onClick={() => navigate("/tarifs")} data-testid="fd-locked-cta"
              className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm">
              <Sparkles size={15} /> {fd.lockedCta}
            </button>
          </motion.div>
        )}

        {!sessionId && !locked && (
          <>
            {/* cinématique en fondu */}
            <div className="mb-8">
              <CinematicVideo fade sound src={DOUTE_VIDEO} testId="doute-cinematic-video" caption={fd.subtitle} />
            </div>
            <form onSubmit={(e) => { e.preventDefault(); start(); }} className="rounded-2xl bg-white/[0.03] border border-white/12 p-3 flex items-center gap-2 mb-4" data-testid="fd-start-form">
              <input value={thesis} onChange={(e) => setThesis(e.target.value)} placeholder={fd.placeholder} data-testid="fd-thesis-input"
                className="flex-1 bg-transparent px-3 py-2 text-sm sm:text-base text-foreground placeholder:text-foreground/35 focus:outline-none" />
              <button type="submit" disabled={starting || !thesis.trim()} data-testid="fd-start"
                className="inline-flex items-center gap-2 rounded-full bg-primary/15 border border-primary/40 text-primary px-5 py-2.5 text-sm font-medium hover:bg-primary/25 transition-colors disabled:opacity-50 flex-shrink-0">
                {starting ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                <span className="hidden sm:inline">{fd.start}</span>
              </button>
            </form>
            {!starting && (
              <div data-testid="fd-presets">
                <p className="text-xs text-foreground/40 mb-2.5">{fd.suggest}</p>
                <div className="flex flex-wrap gap-2">
                  {presets.map((p, i) => (
                    <button key={i} onClick={() => start(p)} data-testid={`fd-preset-${i}`}
                      className="text-xs sm:text-sm px-3.5 py-2 rounded-full border border-primary/20 text-foreground/70 hover:text-primary hover:bg-primary/[0.06] hover:border-primary/40 transition-colors">
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {starting && <AiThinking variant="doute" testId="fd-starting" />}
            {!starting && history.length > 0 && (
              <div className="mt-7" data-testid="fd-history">
                <p className="text-xs text-foreground/40 mb-2.5 flex items-center gap-1.5"><History size={13} /> {ht.title}</p>
                <div className="flex flex-wrap gap-2">
                  {history.map((h) => (
                    <button key={h.id} onClick={() => openSession(h.id)} data-testid={`fd-history-item`}
                      className="group flex items-center gap-2 text-xs sm:text-sm pl-3.5 pr-2 py-1.5 rounded-full border border-white/10 text-foreground/60 hover:text-primary hover:bg-primary/[0.06] hover:border-primary/40 transition-colors max-w-full">
                      <span className="truncate max-w-[220px]">{h.thesis}</span>
                      <span className="flex-shrink-0 inline-flex items-center gap-1 tabular-nums font-semibold px-2 py-0.5 rounded-full text-[0.7rem] border border-red-400/30 bg-red-400/10 text-red-300" title={ht.exch}>
                        <Flame size={11} /> {h.exchanges}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {sessionId && (
          <div className="space-y-4" data-testid="fd-chat">
            <div className="text-center text-xs text-foreground/45 italic mb-1">« {thesis} »</div>
            <div className="flex justify-center"><DialecticCore variant="ember" intensity={devilCount} pulseKey={devilCount} testId="fd-core" /></div>
            <AnimatePresence>
              {messages.map((m, i) => {
                const devil = m.role === "devil";
                return (
                  <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
                    className={`flex ${devil ? "justify-start" : "justify-end"}`} data-testid={`fd-msg-${i}`}>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-3 border ${devil ? "bg-red-400/[0.06] border-red-400/25 rounded-tl-sm" : "bg-primary/[0.06] border-primary/25 rounded-tr-sm"}`}>
                      <p className={`font-display text-xs mb-1.5 flex items-center gap-1.5 ${devil ? "text-red-300" : "text-primary"}`}>
                        {devil ? <Flame size={13} /> : <User size={13} />} {devil ? fd.devil : fd.you}
                      </p>
                      <p className="text-sm sm:text-[0.95rem] text-foreground/85 leading-relaxed">{m.text}</p>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
            {sending && (
              <div className="flex items-center gap-2 text-foreground/40 text-xs italic pl-1" data-testid="fd-typing">
                <Loader2 size={13} className="animate-spin" /> {fd.loading}
              </div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="glass rounded-2xl border border-white/10 p-3 flex items-center gap-2 sticky bottom-0" data-testid="fd-reply-form">
              <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder={fd.replyPlaceholder} data-testid="fd-reply-input"
                className="flex-1 bg-transparent px-3 py-2 text-sm sm:text-base text-foreground placeholder:text-foreground/35 focus:outline-none" />
              <button type="submit" disabled={sending || !reply.trim()} data-testid="fd-send"
                className="inline-flex items-center gap-2 rounded-full bg-primary/15 border border-primary/40 text-primary px-5 py-2.5 text-sm font-medium hover:bg-primary/25 transition-colors disabled:opacity-50 flex-shrink-0">
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
                <span className="hidden sm:inline">{fd.send}</span>
              </button>
            </form>
            <ShareBar
              lang={lang()}
              endpoint="/dialectic/report-card"
              payload={{
                mode: "doute",
                eyebrow: fd.eyebrow,
                question: thesis,
                left_label: st.convictionLabel,
                right_label: st.doubtLabel,
                exchanges: messages,
                lang: lang(),
              }}
              summary={`« ${thesis} » — ${fd.eyebrow} · COCOON MIND. ${st.cta} cocoonmind.com`}
              title={fd.eyebrow}
              subject={fd.title}
              filename="cocoon-feu-du-doute.png"
              testPrefix="fd-share"
              className="pt-1"
            />
            <div className="text-center pt-1">
              <button onClick={reset} data-testid="fd-reset" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm text-foreground/60 hover:text-primary border border-white/10 hover:border-primary/30 transition-colors">
                <RotateCcw size={15} /> {fd.reset}
              </button>
            </div>
            <div ref={bottomRef} />
          </div>
        )}
      </div>
    </div>
  );
}
